import FetchService from "@core-ui/ApiServices/FetchService";
import type Url from "@core-ui/ApiServices/Types/Url";
import { ClientCacheZone, clientCache } from "@core-ui/cache/ClientCache";
import type { PropertyValue, ViewRenderGroup } from "@ext/properties/models";
import type { Display } from "@ext/properties/models/display";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";

interface UseViewRenderDataProps {
	url: Url | undefined;
	defs: PropertyValue[];
	orderby: PropertyValue[];
	groupby: string[];
	select: string[];
	display: Display;
}

interface ResolvedViewRenderData {
	key: string;
	content: ViewRenderGroup[];
}

export const useViewRenderData = ({
	url,
	defs,
	orderby,
	groupby,
	select,
	display,
}: UseViewRenderDataProps): ViewRenderGroup[] | null => {
	const body = useMemo(
		() => JSON.stringify({ defs, orderby, groupby, select, display }),
		[defs, orderby, groupby, select, display],
	);
	const urlString = url?.toString();
	const key = urlString ? `${urlString}\0${body}` : null;
	const revision = useSyncExternalStore(clientCache.subscribe, clientCache.getRevision, clientCache.getRevision);
	const [resolved, setResolved] = useState<ResolvedViewRenderData | null>(null);
	const cached =
		typeof window !== "undefined" && key
			? clientCache.get<ViewRenderGroup[]>(ClientCacheZone.ViewRenderData, key)
			: undefined;
	const content = cached ?? (resolved?.key === key ? resolved.content : null);

	useEffect(() => {
		if (!url || !key) return;
		if (clientCache.get(ClientCacheZone.ViewRenderData, key) !== undefined) return;
		let mounted = true;

		const load = () =>
			clientCache.getOrCreate<ViewRenderGroup[]>(ClientCacheZone.ViewRenderData, key, async () => {
				const response = await FetchService.fetch(url, body);
				if (!response.ok) throw new Error(`Failed to load view render data: ${response.status}`);
				return (await response.json()) as ViewRenderGroup[];
			});

		void load().then(
			(nextContent) => {
				if (mounted && clientCache.getRevision() === revision) {
					setResolved({ key, content: nextContent });
				}
			},
			() => undefined,
		);
		return () => {
			mounted = false;
		};
	}, [body, key, revision, url]);

	return key ? content : [];
};
